import { useState } from "react";
import { Alert, Button, Snackbar, type ButtonProps } from "@mui/material";
import { EventAvailable as AuditIcon } from "@mui/icons-material";
import ScheduleMeetingDialog from "@/components/ScheduleMeetingDialog";
import type { CRMIndividual } from "@/types";

interface Props {
  companyId?: string;
  companyName?: string | null;
  individuals: CRMIndividual[];
  defaultIndividualId?: string;
  // Visual sizing inherits from MUI Button.
  variant?: ButtonProps["variant"];
  size?: ButtonProps["size"];
  fullWidth?: boolean;
}

/**
 * One-click shortcut to schedule a free Human Performance Audit — opens
 * the standard ScheduleMeetingDialog pre-filled for a 60-min slot with
 * an audit-shaped summary. Removes a friction step from Evelina's
 * LinkedIn-to-audit conversion flow (Wave 2 of the LinkedIn growth plan).
 */
export default function QuickScheduleAuditButton({
  companyId,
  companyName,
  individuals,
  defaultIndividualId,
  variant = "outlined",
  size = "small",
  fullWidth = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: "",
  });

  const summaryDefault = companyName
    ? `Human Performance Audit — ${companyName}`
    : "Human Performance Audit";

  return (
    <>
      <Button
        variant={variant}
        size={size}
        startIcon={<AuditIcon />}
        onClick={() => setOpen(true)}
        fullWidth={fullWidth}
      >
        Schedule audit
      </Button>

      <ScheduleMeetingDialog
        open={open}
        onClose={() => setOpen(false)}
        companyId={companyId}
        individuals={individuals}
        defaultIndividualId={defaultIndividualId}
        defaultSummary={summaryDefault}
        defaultDurationMin={60}
        onScheduled={(result) => {
          setSnackbar({
            open: true,
            message: result.html_link
              ? "Audit scheduled — invite sent + on your calendar"
              : "Audit scheduled",
          });
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity="success" variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
